/**
 * Amortization Schedule Component
 * Generates and displays/exports payment schedule
 */
import React, { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Calendar, Download, FileText, TrendingDown, Printer } from "lucide-react";

export const AmortizationSchedule = ({
  principal,
  annualRate,
  termMonths,
  startDate = new Date(),
}) => {
  const [showFull, setShowFull] = useState(false);

  // Calculate amortization schedule
  const schedule = useMemo(() => {
    if (!principal || !annualRate || !termMonths) return [];

    const monthlyRate = annualRate / 100 / 12;
    const monthlyPayment = monthlyRate === 0
      ? principal / termMonths
      : (principal * monthlyRate * Math.pow(1 + monthlyRate, termMonths)) /
        (Math.pow(1 + monthlyRate, termMonths) - 1);

    let balance = principal;
    const payments = [];
    const start = new Date(startDate);

    for (let month = 1; month <= termMonths; month++) {
      const interestPayment = balance * monthlyRate;
      const principalPayment = monthlyPayment - interestPayment;
      balance = Math.max(0, balance - principalPayment);

      const paymentDate = new Date(start);
      paymentDate.setMonth(paymentDate.getMonth() + month);

      payments.push({
        month,
        date: paymentDate,
        payment: monthlyPayment,
        principal: principalPayment,
        interest: interestPayment,
        balance: balance,
      });
    }

    return payments;
  }, [principal, annualRate, termMonths, startDate]);

  // Calculate totals
  const totals = useMemo(() => {
    if (schedule.length === 0) return { totalPayment: 0, totalInterest: 0, totalPrincipal: 0 };
    
    return {
      totalPayment: schedule.reduce((sum, p) => sum + p.payment, 0),
      totalInterest: schedule.reduce((sum, p) => sum + p.interest, 0),
      totalPrincipal: schedule.reduce((sum, p) => sum + p.principal, 0),
    };
  }, [schedule]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  // Export to CSV
  const exportCSV = () => {
    const headers = ['Month', 'Payment Date', 'Payment', 'Principal', 'Interest', 'Remaining Balance'];
    const rows = schedule.map(p => [
      p.month,
      formatDate(p.date),
      p.payment.toFixed(2),
      p.principal.toFixed(2),
      p.interest.toFixed(2),
      p.balance.toFixed(2),
    ]);

    // Add totals row
    rows.push([
      'TOTALS',
      '',
      totals.totalPayment.toFixed(2),
      totals.totalPrincipal.toFixed(2),
      totals.totalInterest.toFixed(2),
      '',
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `amortization_schedule_${principal}_${termMonths}mo.csv`;
    link.click();
  };

  // Print schedule
  const printSchedule = () => {
    const printContent = `
      <html>
        <head>
          <title>Loan Amortization Schedule - Tzur Finances</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #0A2540; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: right; }
            th { background-color: #0A2540; color: white; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            .summary { margin-top: 20px; padding: 15px; background: #f0f9ff; border-radius: 8px; }
            .footer { margin-top: 30px; font-size: 12px; color: #666; text-align: center; }
          </style>
        </head>
        <body>
          <h1>Loan Amortization Schedule</h1>
          <p><strong>Principal:</strong> ${formatCurrency(principal)} | <strong>Rate:</strong> ${annualRate}% | <strong>Term:</strong> ${termMonths} months</p>
          
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Payment Date</th>
                <th>Payment</th>
                <th>Principal</th>
                <th>Interest</th>
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              ${schedule.map(p => `
                <tr>
                  <td>${p.month}</td>
                  <td style="text-align: left">${formatDate(p.date)}</td>
                  <td>${formatCurrency(p.payment)}</td>
                  <td>${formatCurrency(p.principal)}</td>
                  <td>${formatCurrency(p.interest)}</td>
                  <td>${formatCurrency(p.balance)}</td>
                </tr>
              `).join('')}
            </tbody>
            <tfoot>
              <tr style="font-weight: bold; background: #e5e7eb;">
                <td colspan="2">TOTALS</td>
                <td>${formatCurrency(totals.totalPayment)}</td>
                <td>${formatCurrency(totals.totalPrincipal)}</td>
                <td>${formatCurrency(totals.totalInterest)}</td>
                <td>-</td>
              </tr>
            </tfoot>
          </table>
          
          <div class="summary">
            <strong>Summary:</strong><br/>
            Total Amount Paid: ${formatCurrency(totals.totalPayment)}<br/>
            Total Interest Paid: ${formatCurrency(totals.totalInterest)}<br/>
            Original Principal: ${formatCurrency(principal)}
          </div>
          
          <div class="footer">
            Generated by Tzur Finances | www.tzurfinances.com | ${new Date().toLocaleDateString()}
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  if (schedule.length === 0) {
    return null;
  }

  const displaySchedule = showFull ? schedule : schedule.slice(0, 6);

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Calendar className="h-5 w-5 text-blue-600" />
              Payment Schedule
            </CardTitle>
            <CardDescription>
              {termMonths} monthly payments of {formatCurrency(schedule[0]?.payment || 0)}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={exportCSV}
              className="flex items-center gap-1"
            >
              <Download className="h-4 w-4" />
              CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={printSchedule}
              className="flex items-center gap-1"
            >
              <Printer className="h-4 w-4" />
              Print
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <p className="text-xs text-blue-600 uppercase tracking-wide">Total Payment</p>
            <p className="text-lg font-bold text-blue-700">{formatCurrency(totals.totalPayment)}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-3 text-center">
            <p className="text-xs text-green-600 uppercase tracking-wide">Principal</p>
            <p className="text-lg font-bold text-green-700">{formatCurrency(totals.totalPrincipal)}</p>
          </div>
          <div className="bg-orange-50 rounded-lg p-3 text-center">
            <p className="text-xs text-orange-600 uppercase tracking-wide">Interest</p>
            <p className="text-lg font-bold text-orange-700">{formatCurrency(totals.totalInterest)}</p>
          </div>
        </div>

        {/* Schedule Table */}
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="w-12">#</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Payment</TableHead>
                <TableHead className="text-right">Principal</TableHead>
                <TableHead className="text-right">Interest</TableHead>
                <TableHead className="text-right">Balance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displaySchedule.map((payment) => (
                <TableRow key={payment.month}>
                  <TableCell className="font-medium">{payment.month}</TableCell>
                  <TableCell>{formatDate(payment.date)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(payment.payment)}</TableCell>
                  <TableCell className="text-right text-green-600">{formatCurrency(payment.principal)}</TableCell>
                  <TableCell className="text-right text-orange-600">{formatCurrency(payment.interest)}</TableCell>
                  <TableCell className="text-right font-medium">{formatCurrency(payment.balance)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Show More Button */}
        {schedule.length > 6 && (
          <div className="mt-4 text-center">
            <Button
              variant="ghost"
              onClick={() => setShowFull(!showFull)}
              className="text-blue-600"
            >
              {showFull ? `Show Less` : `Show All ${schedule.length} Payments`}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AmortizationSchedule;
